/**
 * Calendar days as `YYYY-MM-DD` in the browser's own time zone, and the range shortcuts a
 * `DatePicker` of type `daterange` offers beside them.
 */

/** Midnight of the same day. **Mutates `date`** and returns it — pass a copy to keep yours. */
export function startOfDay(date: Date): Date {
  date.setHours(0, 0, 0, 0)
  return date
}

function daysAgo(days: number): Date {
  const date = new Date()
  date.setDate(date.getDate() - days)

  return startOfDay(date)
}

function today(): Date {
  return startOfDay(new Date())
}

function monthStart(monthsBack: number): Date {
  const date = new Date()
  date.setDate(1)
  date.setMonth(date.getMonth() - monthsBack)

  return startOfDay(date)
}

function monthEnd(monthsBack: number): Date {
  const date = monthStart(monthsBack)
  date.setMonth(date.getMonth() + 1)
  date.setDate(0)

  return startOfDay(date)
}

function thisMonthEnd(): Date {
  const end = monthEnd(0)

  return end > today() ? today() : end
}

/** None reaches into the future: "This month" stops at today. Pair with `isFutureDate`. */
export const DATE_RANGE_SHORTCUTS = [
  { text: 'Today', value: (): [Date, Date] => [today(), today()] },
  { text: 'Yesterday', value: (): [Date, Date] => [daysAgo(1), daysAgo(1)] },
  { text: '7 days', value: (): [Date, Date] => [daysAgo(6), today()] },
  {
    text: 'This month',
    value: (): [Date, Date] => [monthStart(0), thisMonthEnd()]
  },
  {
    text: 'Last month',
    value: (): [Date, Date] => [monthStart(1), monthEnd(1)]
  },
  { text: 'Last 30 days', value: (): [Date, Date] => [daysAgo(29), today()] }
]

export function isFutureDate(date: Date): boolean {
  return date.getTime() > Date.now()
}

/**
 * Not `toISOString().slice(0, 10)`: that is the date in UTC, which is a different day for
 * most of the world for part of every day.
 */
export function toIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${date.getFullYear()}-${month}-${day}`
}

/** `2026-09-10` → `10.09.2026`. */
export function isoToDayFirst(iso: string): string {
  const [year, month, day] = iso.split('-')

  return `${day}.${month}.${year}`
}

export function isoToday(): string {
  return toIsoDate(today())
}

export function isoDaysAgo(days: number): string {
  return toIsoDate(daysAgo(days))
}

export function isoMonthStart(): string {
  return toIsoDate(monthStart(0))
}
